import { navigate, RouteComponentProps } from '@reach/router';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import css from './TrainingRequestsForm.module.scss';
import { Input } from '../Input/Input';
import { escapeHtml, logger, mapSerialNumbers } from '../../utils';
import { Button } from '../Button/Button';
import { api } from '../../state/effects';
import { EmailStatus, EmailType, TrainingRequest, TrainingType } from '../../../interfaces/customer-api';
import { useOvermindState } from '../../state';
import { Dropdown } from '../DropDown/DropDown';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import styled from '@mui/material/styles/styled';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Spinner } from '../Spinner/Spinner';
import { InputDropDown } from '../InputDropdown/InputDropDown';
import { CompleteRequestDialog } from '../CompleteRequestDialog/CompleteRequestDialog';

const emailBodyHtmlBuilder = (
    userName: string,
    customerNumber: string,
    phoneNumber: string,
    email: string,
    trainingType: string,
    serialNumber: string,
    numberOfParticipants: string,
    description: string,
    preferredDate: string,
): string => {
    const htmlTemplate = `
        <p>A training request was created by customer:</p>
        <p><b>Username:</b> ${escapeHtml(userName)}</p>
        <p><b>Customer number:</b> ${escapeHtml(customerNumber)}</p>
        <p><b>Phone number:</b> ${escapeHtml(phoneNumber)}</p>
        <p><b>Email:</b> ${escapeHtml(email)}</p>
        <p><b>Training type:</b> ${escapeHtml(trainingType)}</p>
        <p><b>Serial number:</b> ${escapeHtml(serialNumber)}</p>
        <p><b>Number of participants:</b> ${escapeHtml(numberOfParticipants)}</p>
        <p><b>Description:</b> ${escapeHtml(description)}</p>
        <p><b>Preferred training day:</b> ${escapeHtml(preferredDate)}</p>
    `;
    // Remove tabs from beginning of each row, so DB won't show unnecessary tabs
    return htmlTemplate.replace(/^\s*\n|\n\s*$/g, '').replace(/^\s*/gm, '');
};

export const TrainingRequestsForm: React.FC<RouteComponentProps> = () => {
    const { t } = useTranslation('TrainingRequests');
    const { user, selectedCustomerNumber, selectedCustomer, equipment } = useOvermindState();
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [email, setEmail] = useState(user?.email || '');
    const [trainingType, setTrainingType] = useState('');
    const [serialNumber, setSerialNumber] = useState('');
    const [numberOfParticipants, setNumberOfParticipants] = useState('');
    const [description, setDescription] = useState('');
    const [preferredDate, setPreferredDate] = useState('');
    const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);

    const orderedEquipment = useMemo(
        () => [...equipment].sort((a, b) => a.serial_number.localeCompare(b.serial_number)),
        [equipment],
    );

    const trainingTypeOptions = Object.values(TrainingType).map((filter: string) => ({
        id: filter,
        label: t(filter),
    }));

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // Reset the error dialog value
        setShowErrorDialog(false);
        setIsButtonDisabled(true);
        setShowSpinner(true);

        if (selectedCustomerNumber && user && user.id && user.email) {
            try {
                const request = await api.insertEmail(selectedCustomerNumber, {
                    userId: user.id,
                    customerNumber: selectedCustomerNumber,
                    userName: user.name || '',
                    customerName: (user?.isAdmin ? selectedCustomer?.name : user?.sapCustomerName) || '',
                    senderEmail: user.email,
                    // sentToEmail: string[],
                    emailSubject: `Kalmar training request for equipment ${serialNumber}`,
                    emailBodyJson: {
                        participants: Number(numberOfParticipants),
                        trainingDate: preferredDate,
                        trainingType: trainingType,
                        serialNumber: serialNumber,
                        description: description,
                    } as TrainingRequest,
                    emailBodyHtml: emailBodyHtmlBuilder(
                        user.name || '',
                        selectedCustomerNumber,
                        phoneNumber,
                        email,
                        trainingType,
                        serialNumber,
                        numberOfParticipants,
                        description,
                        preferredDate,
                    ),
                    type: EmailType.RequestTraining,
                    status: EmailStatus.Created,
                    createdOn: Date.now().toString(),
                });
                if (request !== null) {
                    setIsCompleteDialogOpen(true);
                } else {
                    setShowErrorDialog(true);
                    setIsCompleteDialogOpen(true);
                }
            } catch (error) {
                setShowErrorDialog(true);
                setIsCompleteDialogOpen(true);
                logger.error('Error sending service request:', error);
            }
        } else {
            logger.info('No customer selected');
        }

        setShowSpinner(false);
        setIsButtonDisabled(false);
    };

    // Check if all required fields are filled
    useEffect(() => {
        const allFilled = [phoneNumber, email, trainingType, description, numberOfParticipants].every(
            (val) => val.toString().trim() !== '',
        );
        const allowedInputs = orderedEquipment.some((eq) => eq.serial_number === serialNumber) && allFilled;
        setIsButtonDisabled(!allowedInputs);
    }, [phoneNumber, email, trainingType, serialNumber, numberOfParticipants, description, orderedEquipment]);

    return (
        <>
            <form id="training-request-form" onSubmit={handleSubmit}>
                <div className={css.formContainer}>
                    <div className={css.formHead}>{t('Details')}</div>
                    <div className={css.formInput}>
                        <span className={css.star}>*</span>
                        {t('Customer number')}
                        <Input
                            inputProps={{
                                value: selectedCustomerNumber || '',
                                disabled: true,
                            }}
                        />
                    </div>
                    <div className={css.formInput}>
                        <span className={css.star}>*</span>
                        {t('User')}
                        <Input
                            inputProps={{
                                value: user?.name,
                                disabled: true,
                            }}
                        />
                    </div>
                    <div className={css.formInput}>
                        <span className={css.star}>*</span>
                        {t('Phone number')}
                        <Input
                            inputProps={{
                                value: phoneNumber,
                                onChange: ({ target }) => {
                                    // Allow only + sign and numbers
                                    const value = target.value.replace(/[^+0-9]/g, '');
                                    setPhoneNumber(value);
                                },
                            }}
                        />
                    </div>
                    <div className={css.formInput}>
                        <span className={css.star}>*</span>
                        {t('Email')}
                        <Input
                            inputProps={{
                                value: email,
                                onChange: ({ target }) => setEmail(target.value),
                            }}
                        />
                    </div>
                    <div className={css.dropdownContainer}>
                        <span className={css.star}>*</span>
                        {t('Training type')}
                        <div className={css.dropdown}>
                            <Dropdown
                                label=""
                                options={trainingTypeOptions}
                                handleChange={(value) => setTrainingType(value)}
                                selected={trainingType}
                            />
                        </div>
                    </div>
                    <div className={css.dropdownContainer}>
                        <span className={css.star}>*</span>
                        {t('Number of participants')}
                        <div className={css.dropdown}>
                            <Dropdown
                                label=""
                                options={[
                                    { id: '1', label: '1' },
                                    { id: '2', label: '2' },
                                    { id: '3', label: '3' },
                                    { id: '4', label: '4' },
                                    { id: '5', label: '5' },
                                    { id: '6', label: '6' },
                                ]}
                                handleChange={(value) => setNumberOfParticipants(value)}
                                selected={numberOfParticipants}
                            />
                        </div>
                    </div>
                    <div className={css.dropdownContainer}>
                        <span className={css.star}>*</span>
                        {t('Serial number')}
                        <div className={css.dropdown}>
                            <InputDropDown
                                options={mapSerialNumbers(orderedEquipment)}
                                value={serialNumber}
                                onChange={(value) => setSerialNumber(value as string)}
                            />
                        </div>
                    </div>
                    <div className={css.description}>
                        <span className={css.star}>*</span>
                        {t('Description')}
                        <Input
                            multiline
                            textareaProps={{
                                value: description,
                                onChange: ({ target }) => setDescription(target.value),
                                placeholder: t('Enter detailed description of the training request...'),
                                rows: 4,
                            }}
                        />
                    </div>
                    <div className={css.formInput}>
                        {t(
                            'Preferred time frame for training (Kalmar can’t guarantee training during this time frame, the training is reserved only after Kalmar has contacted you)',
                        )}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <StyledDatePicker
                                // TODO: Fix styling
                                disablePast={true}
                                slotProps={{
                                    leftArrowIcon: { sx: { width: '2rem', height: '2rem' } },
                                    rightArrowIcon: { sx: { width: '2rem', height: '2rem' } },
                                    monthButton: { sx: { fontSize: '1.6rem' } },
                                    yearButton: { sx: { fontSize: '1.6rem' } },
                                    day: { sx: { fontSize: '1.6rem' } },
                                }}
                                onChange={(newValue) => setPreferredDate(newValue?.format('YYYY-MM-DD') ?? '')}
                            />
                        </LocalizationProvider>
                    </div>
                    <div className={css.buttonContainer}>
                        <Button
                            id="cancel-button"
                            type="button"
                            variant="primary"
                            className={css.button}
                            onClick={() => navigate(-1)} /* Navigate back */
                        >
                            {t('Cancel')}
                        </Button>
                        <Button
                            id="send-button"
                            type="submit"
                            variant="primary"
                            className={css.button}
                            isDisabled={isButtonDisabled}
                        >
                            {showSpinner ? (
                                <div className={css.spinner}>
                                    <Spinner size={2} />
                                </div>
                            ) : (
                                t('Send')
                            )}
                        </Button>
                    </div>
                </div>
            </form>
            {isCompleteDialogOpen && (
                <CompleteRequestDialog
                    setIsCompleteDialogOpen={setIsCompleteDialogOpen}
                    isErrorDialog={showErrorDialog}
                    headerText={showErrorDialog ? t('Request submission failed') : t('Request received')}
                    successText={t(
                        'Thank you for your service request. We will contact you by phone as soon as possible during business hours.',
                    )}
                    errorText={t('Something went wrong')}
                    closeButtonText={t('Close')}
                    onClose={() => {
                        if (!showErrorDialog) navigate('/training-requests');
                    }}
                />
            )}
        </>
    );
};

const StyledDatePicker = styled(DatePicker)(() => ({
    width: '100%',
    marginTop: '0.5rem',
    '& .MuiPickersOutlinedInput-root': {
        borderRadius: '2rem',
        fontSize: '1.6rem',
    },

    '& .MuiSvgIcon-root': {
        width: '2rem',
        height: '2rem',
        color: '#5e2460',
    },

    // TODO: Fixme: fontSize not working
    '& .MuiButtonBase-root-MuiPickersDay-root': {
        fontSize: '1.6rem',
    },
    '& .MuiButtonBase-root': {
        fontSize: '1.6rem',
    },
}));
